import {useCallback, useMemo, useEffect} from 'react';
import {KioskoContext} from './KioskoContext';
import {useKioskoConnection} from './hooks/useKioskoConnection';
import {useKioskoStore} from './useKioskoStore';
import {useToggleKioskoMode} from './hooks/useToggleKioskoMode';
import ChekinProSettings from '../../ChekinProSettings';
import {LAYOUT_MODE_KEY} from 'utils/constants';
import type {
  KioskoProviderProps,
  KioskoContextValue,
  KioskoWebSocketResponse,
} from './types';

export const KioskoProvider = ({
  children,
  autoConnect = true,
  apiKey,
}: KioskoProviderProps) => {
  const {
    setScannerState,
    resetScannerState,
    setConnectionState,
    setApiKey: setStoreApiKey,
    setPrinterState,
    connection: {isConnected},
  } = useKioskoStore();

  const {isKioskoActivated} = useToggleKioskoMode();

  useEffect(() => {
    if (apiKey) {
      setStoreApiKey(apiKey);
    }
  }, [apiKey, setStoreApiKey]);

  useEffect(
    function setLayoutMode() {
      const layoutMode = isConnected ? 'kiosko' : 'default';
      document.body.setAttribute('data-layout-mode', layoutMode);
      if (isConnected) {
        localStorage.setItem(LAYOUT_MODE_KEY, 'kiosko');
      }

      return () => {
        document.body.removeAttribute('data-layout-mode');
      };
    },
    [isConnected],
  );
  const enabled = isKioskoActivated && !ChekinProSettings.isSDK;

  const {disconnect, sendMessage, subscribeToEvents} = useKioskoConnection({
    autoConnect,
    apiKey,
    enabled,
    onConnectionStateChange: setConnectionState,
  });

  const handleScannerInitialized = useCallback(() => {
    setScannerState({isInitialized: true});
  }, [setScannerState]);

  const handleDocumentScanned = useCallback(
    (data: KioskoWebSocketResponse) => {
      setScannerState({
        lastScanResult: data.scan_data ?? null,
        isScanning: false,
        error: data.error ?? '',
        success: data.success,
      });
    },
    [setScannerState],
  );

  const handlePrintedCardSuccess = useCallback(() => {
    setPrinterState({
      isPrintingSuccess: true,
      isPrintingError: false,
    });
  }, [setPrinterState]);

  const handlePrintedCardError = useCallback(() => {
    setPrinterState({
      isPrintingError: true,
      isPrintingSuccess: false,
    });
  }, [setPrinterState]);

  const handleResetScannerState = useCallback(() => {
    resetScannerState();
  }, [resetScannerState]);

  useEffect(() => {
    const unsubscribe = subscribeToEvents({
      onScannerInitialized: handleScannerInitialized,
      onDocumentScanned: handleDocumentScanned,
      onPrintedCard: handlePrintedCardSuccess,
      onPrintedCardError: handlePrintedCardError,
    });

    return unsubscribe;
  }, [
    subscribeToEvents,
    handleScannerInitialized,
    handleDocumentScanned,
    handlePrintedCardSuccess,
    handlePrintedCardError,
  ]);

  const contextValue: KioskoContextValue = useMemo(
    () => ({
      disconnect,
      sendMessage,
      subscribeToEvents,
      handleResetScannerState,
    }),
    [disconnect, sendMessage, subscribeToEvents, handleResetScannerState],
  );

  return <KioskoContext.Provider value={contextValue}>{children}</KioskoContext.Provider>;
};
